import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { api } from '../state/auth.js';
import { useAuth } from '../hooks/useAuth.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import { parseServerDate } from '../utils/dates.js';
import { translateError } from '../i18n.js';
import { DepartmentLabelById } from '../components/shared/DepartmentLabel.jsx';

// ── Helpers ───────────────────────────────────────────────────────────────────

const RECURRENCE_LABELS = {
  once:    'Eenmalig',
  daily:   'Dagelijks',
  weekly:  'Wekelijks',
  monthly: 'Maandelijks',
  yearly:  'Jaarlijks',
};

const MONTHS = [
  'Januari','Februari','Maart','April','Mei','Juni',
  'Juli','Augustus','September','Oktober','November','December'
];

function formatDate(str) {
  if (!str) return '—';
  return parseServerDate(str).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
}

function isOverdue(dateStr) {
  return dateStr && new Date(dateStr) < new Date();
}

function recurrenceDescription(rec) {
  switch (rec.recurrence_type) {
    case 'once':    return 'Eenmalig';
    case 'daily':   return 'Elke dag';
    case 'weekly':  return 'Elke week';
    case 'monthly': return rec.recurrence_day ? `Elke maand op de ${rec.recurrence_day}e` : 'Elke maand';
    case 'yearly':
      const m = rec.recurrence_month ? MONTHS[rec.recurrence_month - 1] : '';
      const d = rec.recurrence_day ? `${rec.recurrence_day} ` : '';
      return `Elk jaar${m ? ` in ${d}${m}` : ''}`;
    default: return rec.recurrence_type;
  }
}

// ── Schedule Form ─────────────────────────────────────────────────────────────

// departmentChoice: GET /api/recurring/start-options when the club has two or more
// departments; the process then starts in the one picked ('main' = the main club)
function ScheduleForm({ templates, members, initial, departmentChoice, onSave, onCancel, saving }) {
  const EMPTY = {
    template_id: '',
    title_template: '',
    recurrence_type: 'yearly',
    recurrence_day: '',
    recurrence_month: '',
    next_run_at: '',
    assigned_to: '',
    is_active: true,
  };
  const [form, setForm] = useState(initial ? {
    ...initial,
    recurrence_day: initial.recurrence_day || '',
    recurrence_month: initial.recurrence_month || '',
    assigned_to: initial.assigned_to || '',
  } : EMPTY);

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  const startDepartment = initial ? initial.department_id : departmentChoice?.defaultId;
  const [department, setDepartment] = useState(startDepartment != null ? String(startDepartment) : 'main');
  const save = () => onSave(departmentChoice ? { ...form, departmentId: department === 'main' ? null : Number(department) } : form);

  // Auto-fill title template when template changes
  function handleTemplateChange(id) {
    set('template_id', id);
    if (!form.title_template && id) {
      const tpl = templates.find(t => String(t.id) === String(id));
      if (tpl) set('title_template', `${tpl.name} {jaar}`);
    }
  }

  const showDay = ['monthly', 'yearly'].includes(form.recurrence_type);
  const showMonth = form.recurrence_type === 'yearly';
  const isValid = form.template_id && form.title_template.trim() && form.next_run_at;

  return (
    <div style="display:flex;flex-direction:column;gap:10px;padding:16px;background:var(--color-bg-secondary);border-radius:10px;border:1px solid var(--color-border)">
      <div style="font-size:13px;font-weight:600;color:var(--color-text-muted);margin-bottom:2px">
        {initial ? 'Planning bewerken' : 'Nieuwe planning aanmaken'}
      </div>

      {/* Template */}
      <div>
        <label class="form-label">Procesjabloon *</label>
        <select class="form-select" style="font-size:13px" value={form.template_id} onChange={e => handleTemplateChange(e.target.value)}>
          <option value="">— Kies een sjabloon —</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>

      {departmentChoice && (
        <div>
          <label class="form-label">Afdeling</label>
          <select class="form-select" style="font-size:13px" value={department} onChange={e => setDepartment(e.target.value)}>
            {departmentChoice.mainClub && <option value="main">Hoofdvereniging (geen afdeling)</option>}
            {departmentChoice.options.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </div>
      )}

      {/* Title template */}
      <div>
        <label class="form-label">Procestitel *</label>
        <input
          class="form-input" style="font-size:13px"
          placeholder="bijv. Ledenadministratie {jaar} of Seizoensopening {seizoen}"
          value={form.title_template}
          onInput={e => set('title_template', e.target.value)}
        />
        <div style="font-size:11px;color:var(--color-text-muted);margin-top:3px">
          Gebruik <code style="background:var(--color-border);padding:1px 4px;border-radius:3px">{'{jaar}'}</code>,
          <code style="background:var(--color-border);padding:1px 4px;border-radius:3px;margin-left:4px">{'{maand}'}</code> of
          <code style="background:var(--color-border);padding:1px 4px;border-radius:3px;margin-left:4px">{'{seizoen}'}</code> als variabelen.
        </div>
      </div>

      {/* Recurrence type */}
      <div>
        <label class="form-label">Herhaling</label>
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          {Object.entries(RECURRENCE_LABELS).map(([val, label]) => (
            <button
              key={val}
              type="button"
              onClick={() => set('recurrence_type', val)}
              style={`padding:5px 12px;border-radius:10px;font-size:12px;cursor:pointer;border:1px solid ${form.recurrence_type === val ? 'var(--color-primary)' : 'var(--color-border)'};background:${form.recurrence_type === val ? 'var(--color-primary)' : 'transparent'};color:${form.recurrence_type === val ? '#fff' : 'var(--color-text-muted)'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Day / Month pickers for monthly/yearly */}
      {(showDay || showMonth) && (
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px">
          {showDay && (
            <div>
              <label class="form-label">Dag van de maand</label>
              <input class="form-input" style="font-size:13px" type="number" min="1" max="31"
                placeholder="bijv. 1"
                value={form.recurrence_day}
                onInput={e => set('recurrence_day', e.target.value ? parseInt(e.target.value) : '')} />
            </div>
          )}
          {showMonth && (
            <div>
              <label class="form-label">Maand</label>
              <select class="form-select" style="font-size:13px" value={form.recurrence_month} onChange={e => set('recurrence_month', e.target.value ? parseInt(e.target.value) : '')}>
                <option value="">— Elke maand —</option>
                {MONTHS.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Next run date */}
      <div>
        <label class="form-label">{form.recurrence_type === 'once' ? 'Datum uitvoering *' : 'Eerste uitvoering *'}</label>
        <input class="form-input" style="font-size:13px" type="date"
          value={form.next_run_at}
          onInput={e => set('next_run_at', e.target.value)} />
      </div>

      {/* Assigned to */}
      <div>
        <label class="form-label">Verantwoordelijke (optioneel)</label>
        <select class="form-select" style="font-size:13px" value={form.assigned_to} onChange={e => set('assigned_to', e.target.value)}>
          <option value="">— Automatisch —</option>
          {members.map(m => <option key={m.id} value={m.id}>{m.display_name || m.email}</option>)}
        </select>
      </div>

      {initial && (
        <label style="display:flex;align-items:center;gap:8px;font-size:13px;cursor:pointer">
          <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
          Actief (wordt automatisch uitgevoerd)
        </label>
      )}

      <div style="display:flex;gap:6px;margin-top:4px">
        <Button variant="primary" size="sm" onClick={save} disabled={saving || !isValid}>
          {saving ? '…' : (initial ? 'Opslaan' : 'Planning aanmaken')}
        </Button>
        <Button variant="secondary" size="sm" onClick={onCancel}>Annuleren</Button>
      </div>
    </div>
  );
}

// ── Main View ─────────────────────────────────────────────────────────────────

export default function RecurringView() {
  const auth = useAuth();
  const canManage = auth.hasPermission('processes', 'create');

  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [departmentChoice, setDepartmentChoice] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api('/api/recurring'),
      api('/api/templates').catch(() => []),
      api('/api/users/members').catch(() => []),
      canManage ? api('/api/recurring/start-options').catch(() => null) : null,
    ]).then(([sched, tpls, mbrs, choice]) => {
      setDepartmentChoice(choice?.show ? choice : null);
      setSchedules(Array.isArray(sched) ? sched : []);
      setTemplates(Array.isArray(tpls) ? tpls.filter(t => t.is_active) : []);
      setMembers(Array.isArray(mbrs) ? mbrs : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleSave(form) {
    setSaving(true);
    setError('');
    try {
      if (editItem) {
        await api(`/api/recurring/${editItem.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setSchedules(s => s.map(r => r.id === editItem.id ? { ...r, ...form, department_id: form.departmentId !== undefined ? form.departmentId : r.department_id } : r));
        setEditItem(null);
      } else {
        const created = await api('/api/recurring', { method: 'POST', body: JSON.stringify(form) });
        setSchedules(s => [...s, { ...created, can_edit: true, can_delete: true }]);
        setShowForm(false);
      }
    } catch (err) { setError(translateError(err.code)); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!confirm('Planning verwijderen? Dit kan niet ongedaan gemaakt worden.')) return;
    setDeletingId(id);
    try {
      await api(`/api/recurring/${id}`, { method: 'DELETE' });
      setSchedules(s => s.filter(r => r.id !== id));
    } catch {}
    setDeletingId(null);
  }

  async function handleToggleActive(item) {
    try {
      await api(`/api/recurring/${item.id}`, {
        method: 'PUT',
        body: JSON.stringify({ ...item, is_active: item.is_active ? 0 : 1 }),
      });
      setSchedules(s => s.map(r => r.id === item.id ? { ...r, is_active: r.is_active ? 0 : 1 } : r));
    } catch {}
  }

  if (loading) {
    return (
      <div>
        <div class="page-header"><h1 class="page-title">Terugkerende processen</h1></div>
        <div class="text-muted text-center" style="padding:60px">Laden…</div>
      </div>
    );
  }

  const active = schedules.filter(s => s.is_active);
  const inactive = schedules.filter(s => !s.is_active);

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">🔁 Terugkerende processen</h1>
        {canManage && !showForm && !editItem && (
          <Button variant="primary" size="sm" onClick={() => setShowForm(true)}>
            + Planning aanmaken
          </Button>
        )}
      </div>

      {/* Info banner */}
      <div style="margin-bottom:20px;padding:12px 16px;background:var(--color-bg-secondary);border-radius:10px;border:1px solid var(--color-border);font-size:13px;color:var(--color-text-muted)">
        🤖 Planningen worden automatisch gecontroleerd bij elke serverstart en daarna elk uur.
        Gebruik variabelen als <code style="background:var(--color-border);padding:1px 4px;border-radius:3px">{'{jaar}'}</code> of <code style="background:var(--color-border);padding:1px 4px;border-radius:3px">{'{seizoen}'}</code> in de procestitel.
      </div>

      {error && <div class="alert alert-error" style="margin-bottom:12px">{error}</div>}

      {/* Create form */}
      {showForm && (
        <div style="margin-bottom:20px">
          <ScheduleForm
            templates={templates}
            members={members}
            departmentChoice={departmentChoice}
            onSave={handleSave}
            onCancel={() => setShowForm(false)}
            saving={saving}
          />
        </div>
      )}

      {/* Edit form */}
      {editItem && (
        <div style="margin-bottom:20px">
          <ScheduleForm
            templates={templates}
            members={members}
            initial={editItem}
            departmentChoice={departmentChoice}
            onSave={handleSave}
            onCancel={() => setEditItem(null)}
            saving={saving}
          />
        </div>
      )}

      {/* Active schedules */}
      {schedules.length === 0 ? (
        <Card>
          <div class="text-center text-muted" style="padding:40px">
            <div style="font-size:32px;margin-bottom:12px">🔁</div>
            <div style="font-size:15px;font-weight:500;margin-bottom:6px">Nog geen planningen</div>
            <div style="font-size:13px">Maak een planning aan om processen automatisch te starten op een vaste datum.</div>
          </div>
        </Card>
      ) : (
        <>
          {active.length > 0 && (
            <Card title={`✅ Actief (${active.length})`}>
              <div style="display:flex;flex-direction:column;gap:8px">
                {active.map(item => (
                  <ScheduleCard
                    key={item.id}
                    item={item}
                    canManage={canManage}
                    onEdit={() => { setEditItem(item); setShowForm(false); }}
                    onDelete={() => handleDelete(item.id)}
                    onToggle={() => handleToggleActive(item)}
                    deleting={deletingId === item.id}
                  />
                ))}
              </div>
            </Card>
          )}

          {inactive.length > 0 && (
            <div style="margin-top:16px">
              <Card title={`⏸ Inactief (${inactive.length})`}>
                <div style="display:flex;flex-direction:column;gap:8px">
                  {inactive.map(item => (
                    <ScheduleCard
                      key={item.id}
                      item={item}
                      canManage={canManage}
                      onEdit={() => { setEditItem(item); setShowForm(false); }}
                      onDelete={() => handleDelete(item.id)}
                      onToggle={() => handleToggleActive(item)}
                      deleting={deletingId === item.id}
                    />
                  ))}
                </div>
              </Card>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ScheduleCard({ item, canManage, onEdit, onDelete, onToggle, deleting }) {
  const overdue = isOverdue(item.next_run_at);

  return (
    <div style={`display:flex;align-items:flex-start;gap:12px;padding:12px 14px;border-radius:10px;border:1px solid ${overdue ? '#f59e0b' : 'var(--color-border)'};background:${overdue ? '#fef9c3' : 'var(--color-bg-secondary)'};opacity:${item.is_active ? 1 : 0.6}`}>
      {/* Icon */}
      <div style="font-size:24px;flex-shrink:0;margin-top:2px">🔁</div>

      {/* Info */}
      <div style="flex:1;min-width:0">
        <div style="font-size:14px;font-weight:600;margin-bottom:2px;display:flex;align-items:center;gap:6px;flex-wrap:wrap">
          {item.title_template}
          <DepartmentLabelById id={item.department_id} />
        </div>
        <div style="font-size:12px;color:var(--color-text-muted);margin-bottom:4px">
          Sjabloon: <strong>{item.template_name}</strong> · {recurrenceDescription(item)}
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:10px;font-size:12px">
          <span style={`${overdue ? 'color:#d97706;font-weight:500' : 'color:var(--color-text-muted)'}`}>
            {overdue ? '⚠️ Gepland: ' : '📅 Volgende uitvoering: '}{formatDate(item.next_run_at)}
          </span>
          {item.last_run_at && (
            <span style="color:var(--color-text-muted)">
              Laatste run: {formatDate(item.last_run_at)}
            </span>
          )}
          {item.assigned_to_name && (
            <span style="color:var(--color-text-muted)">👤 {item.assigned_to_name}</span>
          )}
        </div>
      </div>

      {/* Actions: the server says per schedule (a role for a department: only its own) */}
      {canManage && item.can_edit !== false && (
        <div style="display:flex;gap:4px;flex-shrink:0">
          <button
            onClick={onToggle}
            title={item.is_active ? 'Pauzeren' : 'Activeren'}
            style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px;color:var(--color-text-muted)"
          >
            {item.is_active ? '⏸' : '▶'}
          </button>
          <button
            onClick={onEdit}
            title="Bewerken"
            style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px"
          >
            ✏️
          </button>
          {item.can_delete !== false && (
            <button
              onClick={onDelete}
              disabled={deleting}
              title="Verwijderen"
              style="background:none;border:1px solid var(--color-border);border-radius:6px;padding:3px 8px;cursor:pointer;font-size:12px"
            >
              🗑️
            </button>
          )}
        </div>
      )}
    </div>
  );
}
